import type { PopoverWidthMultipliers } from 'theme-utils/types';
import type { ThemeSpaces } from '@frameio/vapor-theme';
import { theme, themeDensitySpacingValues } from '@frameio/vapor-theme';
import { useDensity } from './themeContextUtils';

/**
 * Get the number value for the intending spacing accounting for the theme density.
 * This is only for places where fixed numbers are necessary for calculations. Otherwise, use `theme.spaces` directly.
 * @param spacing - The number to get the adaptive spacing for
 * @returns The adaptive spacing number
 */
export const useGetAdaptiveSpacingNumber = ({
  spacing,
}: {
  spacing?: number;
}) => {
  const density = useDensity();
  if (!spacing) {
    return 0;
  }

  return (
    themeDensitySpacingValues[spacing as ThemeSpaces]?.[density ?? 'regular'] ??
    spacing
  );
};

/**
 * Get the css value for the intending spacing. Uses the theme CSS vars if possible. Otherwise, it returns the number with a px suffix.
 * This is only for places where fixed numbers are necessary for calculations. Otherwise, use `theme.spaces` directly and enforce only using theme spacing numbers using the `ThemeSpaces` type.
 * @param spacing - The number to get the adaptive spacing for
 * @returns The adaptive spacing css value
 */
export const getSpacingCSS = ({ spacing }: { spacing?: number }) => {
  if (!spacing) {
    return 0;
  }

  return theme.spaces[spacing as ThemeSpaces] ?? `${spacing}px`;
};

/**
 * Get the css value for the adaptive modal width. Uses `theme.spaces[64]` as the base width.
 *
 * @example
 * ```tsx
 * const width = getAdaptivePopoverWidthCSS({ multiplier: 3 }); // 'calc(var(--spaces-64) * 3)'
 * ```
 */
export const getAdaptivePopoverWidthCSS = ({
  multiplier,
}: {
  multiplier: PopoverWidthMultipliers;
}) => {
  return `calc(${theme.spaces[64]} * ${multiplier})`;
};
